import { useState, useEffect } from 'react';
import { getNarrationPrefix } from '../../../../features/scenarios/downloadUtils';
import { Tooltip } from '../../../common/Tooltip';
import { NarrationHistorySelector } from '../NarrationHistorySelector';
import { MergeNarrationButton } from '../MergeNarrationButton';
import { NarrationPlayer } from '../NarrationPlayer';
import { SoundLogoSlot } from '../SoundLogoSlot';
import { SoundLogoPlayer } from '../SoundLogoPlayer';
import { NarrationThreeDotMenu } from './NarrationThreeDotMenu';
import styles from '../ScenarioGenerator.module.css';
import { useSceneStateContext } from '../hooks/SceneStateContext';
import { useSceneHandlers } from '../hooks/SceneHandlersContext';
import { resolveNarrationText } from '../narrationUtils';
import { isLyricsModeScenario } from '../hooks/useBgmEditor';
import type { SceneItemProps } from '../SceneItem';
import { useCreditRates } from '../../../../features/organizations/hooks';
import { isMotionV2Script, narrationVoiceSpeaker } from '../../../../features/scenarios/scriptContract';
import type { DialogueLine } from '../../../../features/scenarios/types';
import { useScenarioVoiceMap } from '../hooks/useScenarioVoiceMap';
import { useSceneVoiceStatus } from '../hooks/useSceneVoiceStatus';
import {
  NARRATION_FIELD_LINE_ID,
  VOICE_LINES_CHANGED_MESSAGE,
  buildSceneLanguages,
  estimateVoiceLinesCredits,
  sceneTtsLines,
  unassignedSpeakerIds,
  usesVoiceLines,
  voiceRegenerateNotice,
  voiceVideoStaleNotice,
} from '../voiceLines';
import { NarrationVoiceLines } from './NarrationVoiceLines';
import { CopyTextButton } from './CopyTextButton';

type SceneNarrationBoxProps = Pick<SceneItemProps, 'scenario' | 'scene' | 'sceneKey'>;

const UNASSIGNED_SPEAKER_MESSAGE = '声が割り当てられていない話者がいます。下の「ナレーション」で声を選んでから生成してください';

/**
 * Step 3「ナレーション / サウンドロゴ」ボックス (フローアクションエリア)。
 *
 * 歌モノ以外のすべてのシナリオで、ナレーションは話者付きの行 (voice-lines) として扱う: 本文欄に TTS の行の一覧
 * (開始秒・行の再生・この行だけ撮り直す・行の鮮度) を出し、「このナレーションだけ生成」はこのシーンの行を読み上げて
 * 連結するジョブ (handleGenerateVoiceLines) を受け付ける。話者の声はシナリオ末尾のナレーションセクションで選ぶ。
 * - motion-v2: dialogue_lines の全行 (編集は台本欄)
 * - 実写: 1 行目がナレーター行 (= narration 欄・鉛筆で編集できる)、続けて声だけの登場人物の行 (編集は台本欄)
 * - 歌モノ: narration は歌詞の正本 (Lyria が歌う) なので本文の表示 / 編集だけ
 * - ブランドカット: narration 文の表示 / 編集だけ (生成ボタンは出さない)
 * - サウンドロゴ: 従来どおり
 */
export function SceneNarrationBox({
  scenario,
  scene,
  sceneKey: key,
}: SceneNarrationBoxProps) {
  const {
    expandedNarrations,
    setExpandedNarrations,
    editingField,
    editingValue,
    setEditingField,
    setEditingValue,
    handleSaveField,
    handleSelectNarration,
    handleSetAudioTrackType,
    handleSelectSoundLogo,
    handleToggleNarrationExcluded,
    handleMergeComplete,
    handleDownloadMaterial,
    narrationBlockedByVocalLeadBgm,
    narrationEnglishScenes,
    setNarrationEnglishScenes,
    voiceNameMap,
    downloadingMaterials,
    fetchMaterialById,
    videoGenerationMode,
    handleGenerateVoiceLines,
    handleRetakeVoiceLine,
    voiceLinesRunning,
  } = useSceneHandlers();
  const { playedNarrations, setPlayedNarrations } = useSceneStateContext();

  // ── 行ベースのナレーション (歌モノ・ブランドカット以外) ──
  const motionV2 = isMotionV2Script(scenario);
  const voiceMode = usesVoiceLines(scenario) && !scene.brand_cut_position;
  const { data: voiceMapData } = useScenarioVoiceMap(scenario.id, usesVoiceLines(scenario));
  const { data: creditRatesData } = useCreditRates();
  const voiceStatus = useSceneVoiceStatus(scenario, scene.id);
  const [voiceMessage, setVoiceMessage] = useState<string | null>(null);
  const [voiceLinesChanged, setVoiceLinesChanged] = useState(false);
  const [voiceBusy, setVoiceBusy] = useState(false);
  const ttsLines = voiceMode ? sceneTtsLines(scenario, scene) : [];
  // 実写は 1 行目を常にナレーター行 (narration 欄) にする。本文が空でも鉛筆で書けるよう空の行を置く
  const displayLines: DialogueLine[] = voiceMode && !motionV2 && ttsLines[0]?.line_id !== NARRATION_FIELD_LINE_ID
    ? [{ line_id: NARRATION_FIELD_LINE_ID, speaker: narrationVoiceSpeaker(), text: '' }, ...ttsLines]
    : ttsLines;
  const voiceRunning = voiceLinesRunning?.[scenario.id] ?? null;
  const voiceSceneRunning = voiceMode && !!voiceRunning
    && (voiceRunning.sceneIds.length === 0 || voiceRunning.sceneIds.includes(scene.id));
  const voiceNotices = voiceMode
    ? [voiceRegenerateNotice(voiceStatus), voiceVideoStaleNotice(voiceStatus)].filter((n): n is string => !!n)
    : [];
  const voiceCredits = voiceMode ? estimateVoiceLinesCredits(voiceMapData?.speakers, [scene.id], creditRatesData?.rates) : null;
  const voiceSummary = ttsLines.map((l) => l.text).filter(Boolean).join(' / ');

  const generateSceneVoiceLines = async () => {
    if (!handleGenerateVoiceLines || voiceBusy || voiceRunning) return;
    if (voiceMapData && unassignedSpeakerIds(voiceMapData.speakers, [scene.id]).length > 0) {
      setVoiceMessage(UNASSIGNED_SPEAKER_MESSAGE);
      return;
    }
    setVoiceBusy(true);
    setVoiceMessage(null);
    try {
      const sceneLanguages = buildSceneLanguages([{ id: scene.id, key }], narrationEnglishScenes);
      const res = await handleGenerateVoiceLines(scenario, [scene.id], sceneLanguages ? { sceneLanguages } : undefined);
      if (res.ok) setVoiceLinesChanged(false);
      else if (res.unassignedSpeakers) setVoiceMessage(UNASSIGNED_SPEAKER_MESSAGE);
      else if (res.message) setVoiceMessage(res.message);
    } finally {
      setVoiceBusy(false);
    }
  };

  const retakeVoiceLine = async (lineId: string) => {
    if (!handleRetakeVoiceLine || voiceBusy || voiceRunning) return;
    setVoiceBusy(true);
    setVoiceMessage(null);
    try {
      const res = await handleRetakeVoiceLine(scenario, scene.id, lineId);
      if (res.ok) setVoiceLinesChanged(false);
      else if (res.linesChanged) setVoiceLinesChanged(true);
      else if (res.message) setVoiceMessage(res.message);
    } finally {
      setVoiceBusy(false);
    }
  };

  // ナレーションアコーディオンの既定開閉状態。制作方式 (シーンごと / ストーリーボード) に
  // かかわらず既定は畳んでおく (2026-09-09: 以前はシーンごとモードで内容があれば開いていた)。
  // 一括生成が標準フローになり、シーン内で開く必要があるのは個別調整のときだけになったため。
  // ユーザーが明示的にトグルした場合は expandedNarrations[key] を優先する。
  const narrationDefaultExpanded = false;
  const isNarrationExpanded = expandedNarrations[key] ?? narrationDefaultExpanded;

  // シーンの前面音声の種別 (narration: TTS / sound_logo: ブランドのサウンドロゴ)。排他。
  const audioMode: 'narration' | 'sound_logo' = scene.audio_track_type === 'sound_logo' ? 'sound_logo' : 'narration';

  // 選択中サウンドロゴの表示名 (スクリプト相当)。ヘッダー (折りたたみ時) と
  // SoundLogoSlot の両方で使うため、ここで一度だけ解決して共有する。
  const [soundLogoName, setSoundLogoName] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    const mid = scene.sound_logo_material_id;
    if (!mid || audioMode !== 'sound_logo') {
      setSoundLogoName(null);
      return;
    }
    fetchMaterialById(mid)
      .then((m: any) => {
        if (cancelled || !m) return;
        const meta = m.generation_metadata || {};
        const title = (meta.song_title || meta.narration_text) as string | undefined;
        setSoundLogoName(title || m.notes || 'サウンドロゴ');
      })
      .catch(() => { /* 取得失敗時は名前なし */ });
    return () => { cancelled = true; };
  }, [scene.sound_logo_material_id, audioMode, fetchMaterialById]);

  // ナレーション文も生成済み音声も無いシーンでは、ナレーション欄は「無い」ことが分かれば十分なので
  // 背景・枠線を落とし太字も解除して存在感を下げる (サウンドロゴ選択時は通常表示)。
  const isNarrationEmpty = audioMode === 'narration' && !scene.narration_material_id
    && (voiceMode ? ttsLines.length === 0 : !resolveNarrationText(scene));
  // 歌モノ (bgm_mode==='lyrics') では narration が歌詞の正本で Lyria が歌う (計画 §3.1)。
  // TTS のシーン単体生成と、サウンドロゴへの切替セレクトは出さない
  // (すでにサウンドロゴが選ばれているシーンだけは戻せるようセレクトを残す)。
  const isLyricsScenario = isLyricsModeScenario(scenario);
  const hideAudioModeSelect = isLyricsScenario && audioMode === 'narration';
  const hideNarrationTts = isLyricsScenario;
  const isNarrationEditing = editingField?.sceneId === scene.id && editingField?.field === 'narration';
  const startNarrationEdit = () => {
    setEditingField({ sceneId: scene.id, field: 'narration' });
    setEditingValue((voiceMode ? scene.narration : resolveNarrationText(scene)) || '');
  };
  // 歌モノでは narration 欄の見出し・文言を「歌詞」に差し替える (データ上のフィールドは narration のまま)。
  const fieldLabel = isLyricsScenario ? '歌詞' : 'ナレーション';
  const fieldLabelShort = isLyricsScenario ? '歌詞' : 'Na';

  // narration 欄のインライン編集 (実写のナレーター行・歌詞・ブランドカットで共用)
  const narrationEditor = (
    <div className={styles.inlineEditContainer} style={{ flex: 1 }} data-scene-inline-edit>
      <img src="/microphone_1.svg" alt={fieldLabel} className={styles.sceneIcon} />
      <textarea
        className={styles.inlineEditTextarea}
        value={editingValue}
        onChange={(e) => setEditingValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.preventDefault();
            setEditingField(null);
            setEditingValue('');
          } else if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
            e.preventDefault();
            handleSaveField(scene.id, 'narration', editingValue);
          }
        }}
        autoFocus
        rows={Math.max(2, editingValue.split('\n').length)}
      />
      <div className={styles.inlineEditActions}>
        <button
          type="button"
          className={styles.inlineEditCancel}
          onClick={() => {
            setEditingField(null);
            setEditingValue('');
          }}
          title="キャンセル (Esc)"
        >
          キャンセル
        </button>
        <button
          type="button"
          className={styles.inlineEditSave}
          onClick={() => handleSaveField(scene.id, 'narration', editingValue)}
          title="保存"
        >
          保存
        </button>
      </div>
    </div>
  );

  return (
                                  <div className={`${styles.flowStepSection} ${styles.narrationStepContainer} ${isNarrationEmpty ? styles.narrationStepContainerEmpty : ''}`}>
                                    <div
                                      className={styles.stepHeader}
                                      onClick={() => {
                                        setExpandedNarrations(prev => ({ ...prev, [key]: !(prev[key] ?? narrationDefaultExpanded) }));
                                      }}
                                      style={{ cursor: 'pointer' }}
                                    >
                                      <div className={styles.stepTitle}>
                                        <span
                                          className={styles.expandTriangle}
                                          style={{ transform: isNarrationExpanded ? 'rotate(90deg)' : 'none' }}
                                        >
                                          <svg width="12" height="12" viewBox="0 0 8 8" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M2 1l4 3-4 3" /></svg>
                                        </span>
                                        {/* 折りたたみ時のみ、生成済み音声があればシェブロンの右に再生ボタン。 */}
                                        {!isNarrationExpanded && audioMode === 'narration' && scene.narration_material_id && (
                                          <span
                                            onClick={(e) => e.stopPropagation()}
                                            style={{ display: 'inline-flex', flexShrink: 0 }}
                                          >
                                            <NarrationPlayer
                                              materialId={scene.narration_material_id}
                                              hasAudio={!!scene.narration_material_id}
                                              unplayed={!!scene.narration_material_id && !playedNarrations.has(key)}
                                              onPlay={() => setPlayedNarrations(prev => new Set(prev).add(key))}
                                              inactive={!!scene.narration_excluded}
                                            />
                                          </span>
                                        )}
                                        {!isNarrationExpanded && audioMode === 'sound_logo' && scene.sound_logo_material_id && (
                                          <span
                                            onClick={(e) => e.stopPropagation()}
                                            style={{ display: 'inline-flex', flexShrink: 0 }}
                                          >
                                            <SoundLogoPlayer materialId={scene.sound_logo_material_id} />
                                          </span>
                                        )}
                                        {audioMode === 'sound_logo' ? (
                                          <span style={{ whiteSpace: 'nowrap' }}>サウンドロゴ</span>
                                        ) : (
                                          <span style={{ whiteSpace: 'nowrap' }}>
                                            <span className={styles.narrationLabelFull}>{fieldLabel}</span>
                                            <span className={styles.narrationLabelShort}>{fieldLabelShort}</span>
                                          </span>
                                        )}
                                        {audioMode === 'sound_logo' && !scene.sound_logo_material_id && <span style={{ fontWeight: 400, color: 'rgba(245, 241, 230, 0.4)', marginLeft: '6px' }}>なし</span>}
                                        {audioMode === 'sound_logo' && !isNarrationExpanded && scene.sound_logo_material_id && soundLogoName && (
                                          <span style={{ fontWeight: 400, color: 'rgba(245, 241, 230, 0.4)', marginLeft: '6px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
                                            {soundLogoName}
                                          </span>
                                        )}
                                        {voiceMode && audioMode === 'narration' && voiceSceneRunning && (
                                          <span style={{ fontWeight: 400, color: 'rgba(245, 241, 230, 0.4)', marginLeft: '6px', whiteSpace: 'nowrap' }}>生成中…</span>
                                        )}
                                        {voiceMode && audioMode === 'narration' && !voiceSceneRunning && !voiceSummary && <span style={{ fontWeight: 400, color: 'rgba(245, 241, 230, 0.4)', marginLeft: '6px' }}>なし</span>}
                                        {voiceMode && audioMode === 'narration' && !voiceSceneRunning && !isNarrationExpanded && voiceSummary && (
                                          <span style={{ fontWeight: 400, color: 'rgba(245, 241, 230, 0.4)', marginLeft: '6px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }} data-testid="narration-voice-summary">
                                            {voiceSummary}
                                          </span>
                                        )}
                                        {!voiceMode && audioMode === 'narration' && !(resolveNarrationText(scene)) && <span style={{ fontWeight: 400, color: 'rgba(245, 241, 230, 0.4)', marginLeft: '6px' }}>なし</span>}
                                        {!voiceMode && audioMode === 'narration' && !isNarrationExpanded && (resolveNarrationText(scene)) && (
                                          <span style={{ fontWeight: 400, color: 'rgba(245, 241, 230, 0.4)', marginLeft: '6px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
                                            {resolveNarrationText(scene)}
                                          </span>
                                        )}
                                      </div>
                                   </div>

                                   <div className={`${styles.expandContent} ${isNarrationExpanded ? styles.expanded : styles.collapsed}`}>
                                      <div className={styles.stepContent}>
                                        {/* 前面音声の種別 (ナレーション / サウンドロゴ)。排他。
                                            ナビゲーション目的なので控えめなシェブロンのセレクトにする。 */}
                                        {!hideAudioModeSelect && (
                                        <select
                                          className={styles.audioModeSelect}
                                          value={audioMode}
                                          onClick={(e) => e.stopPropagation()}
                                          onChange={(e) => {
                                            const next = e.target.value as 'narration' | 'sound_logo';
                                            if (audioMode !== next) handleSetAudioTrackType(scene.id, next);
                                          }}
                                        >
                                          <option value="narration">ナレーション</option>
                                          <option value="sound_logo">サウンドロゴ</option>
                                        </select>
                                        )}

                                        {audioMode === 'sound_logo' && (
                                          <SoundLogoSlot
                                            sceneId={scene.id}
                                            materialId={scene.sound_logo_material_id}
                                            displayName={soundLogoName}
                                            onSelect={handleSelectSoundLogo}
                                            onClear={(sid) => handleSetAudioTrackType(sid, 'narration')}
                                          />
                                        )}

                                        {audioMode === 'narration' && (<>
                                        <div className={styles.sceneFieldRow}>
                                          {voiceMode ? (
                                            <NarrationVoiceLines
                                              lines={displayLines}
                                              materialId={scene.narration_excluded ? null : scene.narration_material_id}
                                              retakingLineId={voiceRunning?.kind === 'retake' ? voiceRunning.lineId ?? null : null}
                                              disabled={voiceBusy || !!voiceRunning}
                                              onRetake={handleRetakeVoiceLine ? (lineId) => void retakeVoiceLine(lineId) : undefined}
                                              editableLineId={motionV2 ? null : NARRATION_FIELD_LINE_ID}
                                              onEditLine={motionV2 ? undefined : startNarrationEdit}
                                              onClearLine={motionV2 ? undefined : () => handleSaveField(scene.id, 'narration', '')}
                                              editingLineId={!motionV2 && isNarrationEditing ? NARRATION_FIELD_LINE_ID : null}
                                              editor={narrationEditor}
                                            />
                                          ) : isNarrationEditing ? narrationEditor : (
                                            <div className={`${styles.narrationTextDisplay} ${!(resolveNarrationText(scene)) ? styles.narrationTextEmpty : ''}`}>
                                              <Tooltip content={isLyricsScenario ? '歌詞（BGM で歌われます）' : 'ナレーション（TTS読み上げ用）'}><img src="/microphone_1.svg" alt={fieldLabel} className={styles.sceneIcon} /></Tooltip>
                                              <span>{resolveNarrationText(scene) || `（${fieldLabel}なし）`}</span>
                                              <button
                                                type="button"
                                                className={styles.fieldEditButton}
                                                onClick={startNarrationEdit}
                                                title={`${fieldLabel}を編集`}
                                              >
                                                <img src="/pencil_1.svg" alt="編集" className={styles.fieldEditIcon} />
                                              </button>
                                              <CopyTextButton text={resolveNarrationText(scene) || ''} label={fieldLabel} />
                                              {resolveNarrationText(scene) && (
                                                <Tooltip content={`${fieldLabel}をクリア`}>
                                                  <button
                                                    type="button"
                                                    className={styles.fieldEditButton}
                                                    onClick={() => handleSaveField(scene.id, 'narration', '')}
                                                    aria-label={`${fieldLabel}をクリア`}
                                                  >
                                                    <img src="/delete_2.svg" alt="" className={styles.fieldEditIcon} />
                                                  </button>
                                                </Tooltip>
                                              )}
                                            </div>
                                          )}
                                        </div>

                                        {voiceMode && (
                                        <div className={styles.sceneAudioRow}>
                                          {(voiceNotices.length > 0 || voiceLinesChanged || voiceMessage) && (
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                                              {voiceNotices.map((n) => <span key={n} className={styles.motionSyncStaleNotice}>{n}</span>)}
                                              {voiceLinesChanged && (
                                                <span className={styles.motionSyncStaleNotice} data-testid="narration-voice-lines-changed">{VOICE_LINES_CHANGED_MESSAGE}</span>
                                              )}
                                              {voiceMessage && <span className={styles.motionSyncStaleNotice} data-testid="narration-voice-message">{voiceMessage}</span>}
                                            </div>
                                          )}
                                          {/* シーン別の「英語で発音する」 */}
                                          <NarrationThreeDotMenu
                                            narrationEnglish={!!narrationEnglishScenes[key]}
                                            setNarrationEnglish={(v) => setNarrationEnglishScenes(prev => ({ ...prev, [key]: v }))}
                                          />
                                          {/* Tooltip でラップされる場合もあるため、右寄せ (margin-left: auto) は外側の div に持たせる */}
                                          <div style={{ marginLeft: 'auto', display: 'inline-flex' }}>
                                          {(() => {
                                            const hasLines = ttsLines.length > 0;
                                            const blockedByVocalLeadBgm = narrationBlockedByVocalLeadBgm;
                                            const generating = voiceSceneRunning && voiceRunning?.kind === 'generate';
                                            const voiceGenerateButton = (
                                              <button
                                                type="button"
                                                className={`${styles.narrationGenerateButton} ${generating ? 'ai-loading-active' : ''}`}
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  void generateSceneVoiceLines();
                                                }}
                                                disabled={!hasLines || blockedByVocalLeadBgm || !handleGenerateVoiceLines || voiceBusy || !!voiceRunning}
                                                {...(hasLines && !blockedByVocalLeadBgm ? { 'data-tooltip': `${voiceCredits ?? '...'}ポイントを消費します`, 'data-tooltip-delay': '' } : {})}
                                              >
                                                {generating ? '生成中...' : (
                                                  <>
                                                    {/* 既にナレーション音声が付いているときは作り直しになるため「再生成」と出す */}
                                                    {scene.narration_material_id ? 'このナレーションだけ再生成' : 'このナレーションだけ生成'}
                                                    <span className={styles.creditBadge}>{voiceCredits ?? '...'}</span>
                                                  </>
                                                )}
                                              </button>
                                            );
                                            if (blockedByVocalLeadBgm) {
                                              return <Tooltip content="採用中の BGM がボーカル主体のため、統合動画ではナレーションは再生されません">{voiceGenerateButton}</Tooltip>;
                                            }
                                            return hasLines ? voiceGenerateButton : (
                                              <Tooltip content="ナレーションの行がありません">{voiceGenerateButton}</Tooltip>
                                            );
                                          })()}
                                          </div>
                                        </div>
                                        )}
                                        </>)}

                                      </div>

                                        {/* 結果エリア (ナレーション時のみ表示。歌モノでは過去に生成した音声があるときだけ) */}
                                        {audioMode === 'narration' && (!hideNarrationTts || !!scene.narration_material_id) && (
                                        <div className={styles.stepResultArea} style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr' }}>
                                          <div style={{ justifySelf: 'start', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                            <NarrationPlayer materialId={scene.narration_material_id} hasAudio={!!scene.narration_material_id} unplayed={!!scene.narration_material_id && !playedNarrations.has(key)} onPlay={() => setPlayedNarrations(prev => new Set(prev).add(key))} inactive={!!scene.narration_excluded} />
                                            <NarrationHistorySelector
                                              sceneId={scene.id}
                                              currentMaterialId={scene.narration_material_id}
                                              onSelect={(materialId, voiceId, durationSeconds) => handleSelectNarration(scene.id, materialId, voiceId, durationSeconds)}
                                              voiceNameMap={voiceNameMap}
                                              selectedVoiceName={scene.narration_voice_id ? voiceNameMap[scene.narration_voice_id] : undefined}
                                              selectedDurationSeconds={scene.narration_duration_seconds}
                                              excluded={!!scene.narration_excluded}
                                              onToggleExcluded={(excluded) => handleToggleNarrationExcluded(scene.id, excluded)}
                                            />
                                          </div>
                                          <div style={{ display: 'flex', alignItems: 'center' }}>
                                            {/* ストーリーボードモードはシナリオ全体で動画 1 本のため、シーン単位の
                                                「動画に合成」は意味を成さない。よって storyboard では出さない。 */}
                                            {videoGenerationMode !== 'storyboard' && scene.narration_material_id && scene.video_material_id && (
                                              <MergeNarrationButton sceneId={scene.id} sceneKey={key} sceneOrder={scene.order} videoHasAudio={scene.video_has_audio} onMerged={(materialId, durationSeconds) => handleMergeComplete(scene.id, key, materialId, durationSeconds)} />
                                            )}
                                          </div>
                                          <div style={{ justifySelf: 'end' }}>
                                            <button
                                              type="button"
                                              className={`${styles.downloadButton}${scene.narration_material_id ? ` ${styles.hasNarration}` : ''}${scene.narration_material_id && downloadingMaterials.has(scene.narration_material_id) ? ` ${styles.downloading}` : ''}`}
                                              disabled={!scene.narration_material_id || (scene.narration_material_id ? downloadingMaterials.has(scene.narration_material_id) : false)}
                                              onClick={() => {
                                                if (scene.narration_material_id) {
                                                  setPlayedNarrations(prev => new Set(prev).add(key));
                                                  const namePrefix = scenario.title.replace(/[\\/:*?"<>|]/g, '_');
                                                  const narrationPart = getNarrationPrefix(resolveNarrationText(scene) || '');
                                                  handleDownloadMaterial(scene.narration_material_id, `${namePrefix}_scene_${scene.order}${narrationPart}_narration`);
                                                }
                                              }}
                                              title={scene.narration_material_id && downloadingMaterials.has(scene.narration_material_id) ? 'ダウンロード中...' : scene.narration_material_id ? 'ナレーション音声をダウンロード' : 'ナレーション未生成'}
                                            >
                                              <img src="/download_bottom.svg" alt="ダウンロード" />
                                            </button>
                                          </div>
                                        </div>
                                        )}

                                        {/* 結果エリア (サウンドロゴ時。ナレーションと同様に再生・DLを最下部に) */}
                                        {audioMode === 'sound_logo' && scene.sound_logo_material_id && (
                                        <div className={styles.stepResultArea} style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr' }}>
                                          <div style={{ justifySelf: 'start', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                            <SoundLogoPlayer materialId={scene.sound_logo_material_id} />
                                            {scene.sound_logo_duration_seconds != null && (
                                              <span className={styles.narrationHistoryDurationLabel}>{scene.sound_logo_duration_seconds}秒</span>
                                            )}
                                          </div>
                                          <div />
                                          <div style={{ justifySelf: 'end' }}>
                                            <button
                                              type="button"
                                              className={`${styles.downloadButton}${scene.sound_logo_material_id ? ` ${styles.hasNarration}` : ''}${scene.sound_logo_material_id && downloadingMaterials.has(scene.sound_logo_material_id) ? ` ${styles.downloading}` : ''}`}
                                              disabled={!scene.sound_logo_material_id || downloadingMaterials.has(scene.sound_logo_material_id)}
                                              onClick={() => {
                                                if (scene.sound_logo_material_id) {
                                                  const namePrefix = scenario.title.replace(/[\\/:*?"<>|]/g, '_');
                                                  handleDownloadMaterial(scene.sound_logo_material_id, `${namePrefix}_scene_${scene.order}_sound_logo`);
                                                }
                                              }}
                                              title={scene.sound_logo_material_id && downloadingMaterials.has(scene.sound_logo_material_id) ? 'ダウンロード中...' : 'サウンドロゴをダウンロード'}
                                            >
                                              <img src="/download_bottom.svg" alt="ダウンロード" />
                                            </button>
                                          </div>
                                        </div>
                                        )}
                                   </div>
                                </div>
  );
}
